import type { TaxCode } from "@/apis/gql/types";

import { useMemo } from "react";
import { AutocompleteInput, type Option } from "./autocomplete-input";

type Props = {
  name: string;
  placeholder: string;
  label?: string;
  options: TaxCode[];
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  side?: "top" | "right" | "bottom" | "left";
  sideOffset?: number;
  autoFocus?: boolean;
  showEmptyLabel?: boolean;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (value: Option["value"]) => void;
  nullable?: boolean;
};

export const TaxCodeAutocompleteInput = ({
  name,
  placeholder,
  sideOffset,
  side,
  label,
  options,
  onBlur,
  onFocus,
  onKeyDown,
  autoFocus,
  showEmptyLabel,
  nullable,
}: Props) => {
  const taxCodeOptions = useMemo(() => {
    return (
      options?.map((taxCode) => ({
        value: taxCode.taxCodeId,
        label: taxCode.name,
        subLabel: taxCode.datevBu.toString(),
      })) ?? []
    );
  }, [options]);

  const filterByBu = (
    inputValue: string,
    value: Option["value"],
    label: Option["label"],
    subLabel: Option["subLabel"],
  ) => {
    const _subLabel = subLabel ?? "";
    const _label = label ?? "";

    if (!inputValue || !/^[0-9]+$/.test(inputValue)) {
      return _label.toLowerCase().includes(inputValue.toLowerCase());
    }

    return _subLabel.toLowerCase().startsWith(inputValue.toLowerCase());
  };

  return (
    <AutocompleteInput
      name={name}
      label={label}
      placeholder={placeholder}
      options={taxCodeOptions}
      onKeyDown={onKeyDown}
      filterBy={filterByBu}
      sideOffset={sideOffset}
      side={side}
      autoFocus={autoFocus}
      onBlur={onBlur}
      onFocus={onFocus}
      showEmptyLabel={showEmptyLabel}
      nullable={nullable}
    />
  );
};
